import { lessonsData } from '../data/lessonsData';

interface LessonContentProps {
  lessonNumber: number;
}

export default function LessonContent({ lessonNumber }: LessonContentProps) {
  const lesson = lessonsData[lessonNumber - 1];

  if (!lesson) return null;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-white rounded-2xl p-8 border border-gray-200">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-[#39FF14] rounded-full flex items-center justify-center">
            <span className="text-black font-bold text-lg">{lessonNumber}</span>
          </div>
          <div>
            <p className="text-sm text-gray-500 font-medium">LECCIÓN {lessonNumber}</p>
            <h2 className="text-3xl font-bold text-black">{lesson.title}</h2>
          </div>
        </div>
        <p className="text-base text-gray-700 leading-relaxed">{lesson.intro}</p>
      </div>

      {/* Explicación */}
      <div className="bg-white rounded-2xl p-8 border border-gray-200">
        <div className="flex items-center gap-3 mb-6">
          <i className="ri-book-open-line text-2xl text-[#1A3A2E]"></i>
          <h3 className="text-2xl font-bold text-black">Explicación</h3>
        </div>
        <div className="prose prose-lg max-w-none">
          {lesson.explanation.map((paragraph, idx) => (
            <p key={idx} className="text-base text-gray-700 leading-relaxed mb-4">
              {paragraph}
            </p>
          ))}
        </div>
      </div>

      {/* Conceptos Clave */}
      {lesson.keyConcepts && (
        <div className="bg-[#F5F2EB] rounded-2xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <i className="ri-lightbulb-line text-2xl text-[#1A3A2E]"></i>
            <h3 className="text-2xl font-bold text-black">Conceptos Clave</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {lesson.keyConcepts.map((concept, idx) => (
              <div key={idx} className="bg-white rounded-xl p-5 border border-gray-200">
                <h4 className="font-bold text-black mb-2">{concept.term}</h4>
                <p className="text-sm text-gray-700 leading-relaxed">{concept.definition}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ejemplo Práctico */}
      <div className="bg-gradient-to-br from-[#1A3A2E] to-[#0D1F17] rounded-2xl p-8 text-white">
        <div className="flex items-center gap-3 mb-6">
          <i className="ri-flask-line text-2xl text-[#39FF14]"></i>
          <h3 className="text-2xl font-bold">Ejemplo Práctico</h3>
        </div>
        <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm">
          <h4 className="font-bold text-[#39FF14] mb-3 text-lg">{lesson.practicalExample.title}</h4>
          <p className="text-gray-200 leading-relaxed mb-4">{lesson.practicalExample.description}</p>
          {lesson.practicalExample.steps && (
            <ul className="space-y-2">
              {lesson.practicalExample.steps.map((step, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-[#39FF14] text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-200 text-sm leading-relaxed">{step}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Error Común */}
      <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-8">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
            <i className="ri-error-warning-line text-2xl text-white"></i>
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-red-900 mb-3">Error Común en Gimnasio</h3>
            <p className="text-base text-red-800 leading-relaxed mb-4">
              <strong>Problema:</strong> {lesson.commonError.problem}
            </p>
            <p className="text-base text-red-800 leading-relaxed">
              <strong>Corrección:</strong> {lesson.commonError.solution}
            </p>
          </div>
        </div>
      </div>

      {/* Aplicación Directa */}
      <div className="bg-white rounded-2xl p-8 border-2 border-[#39FF14]">
        <div className="flex items-center gap-3 mb-6">
          <i className="ri-tools-line text-2xl text-[#1A3A2E]"></i>
          <h3 className="text-2xl font-bold text-black">Aplicación Directa al Entrenamiento</h3>
        </div>
        <div className="space-y-4">
          {lesson.directApplication.map((application, idx) => (
            <div key={idx} className="flex items-start gap-3 bg-[#F5F2EB] rounded-xl p-5">
              <div className="w-8 h-8 bg-[#39FF14] rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-check-line text-black text-lg"></i>
              </div>
              <p className="text-base text-gray-700 leading-relaxed flex-1">{application}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}